<script setup lang="ts">
import { computed, nextTick, onMounted, Ref, ref, watch } from 'vue';
import { VDataTable, VDataTableServer } from 'vuetify/components';
import { useScroll } from '@vueuse/core';
import { FetchItems, SortOrder } from './types/data-table';

type DataTableItem = Required<VDataTable['$props']>['items'][number];
type DataTableOptionsAll = VDataTable['$props'];
type DataTableOptions = Pick<DataTableOptionsAll, 'page' | 'itemsPerPage' | 'sortBy' | 'search'>;

type SearchObject = {
  searchString?: string;
  filters?: Record<string, string>;
  organizationUid?: string;
};

const props = withDefaults(
  defineProps<{
    // organizationUid is here just to trigger data refetching when it changes,
    // So we dont have do that for every table using this component
    organizationUid?: string;
    itemValue?: string;
    itemsPerPage: number;
    searchString?: string;
    sortBy?: string;
    sortOrder?: SortOrder;
    filters?: { [key: string]: any } | undefined;
    fetchItems: FetchItems<(...args: any) => Promise<any>>;
    height?: VDataTableServer['$props']['height'];
    itemsToPrepend?: DataTableItem[];
  }>(),
  {
    organizationUid: undefined,
    itemValue: 'uid',
    itemsPerPage: 50,
    searchString: undefined,
    sortBy: undefined,
    filters: undefined,
    height: 320,
    itemsToPrepend: () => [],
    loading: false,
  },
);

defineExpose({
  dataTable: computed(() => theTable.value),
  listItems: computed(() => listItems.value),
  reset: () => {
    fetchedPages.value = {};
    listItems.value = [];
    listItemsLength.value = 0;
    pageNumber.value = 1;
    internalLoading.value = false;
    scrolling.value = false;
  },
  reloadItems: async () => {
    await nextTick(async () => {
      await optionsUpdated(dataTableOptions.value);
    });
  },
});

defineSlots<{
  bottom: VDataTableServer['$slots']['bottom'];
  [key: string]: (args: any) => any;
}>();

const theTable = ref<VDataTable | null>(null);
const listItems = ref<DataTableItem>([]);
const listItemsLength = ref(0);
const pageNumber = ref(1);
const fetchedPages = ref<Record<number, DataTableItem[]>>({}); // { 1: [], 2: []... }
const selectedItems = defineModel<string[]>({ default: [] });

const internalLoading = ref(false);
const scrolling = ref(false);
const isLoading = computed(() => props.loading || internalLoading.value);

const sortingUpdated = () => {
  pageNumber.value = 1;
  fetchedPages.value = {};
};

const dataTableOptions = ref({} as DataTableOptions);
const optionsUpdated = async (options: DataTableOptions) => {
  dataTableOptions.value = options;
  const { page, itemsPerPage, sortBy, search } = options;
  const searchString = JSON.parse(search ?? '{}').searchString || undefined;
  return fetchItems(
    Number(page),
    itemsPerPage as number,
    sortBy?.[0]?.key,
    sortBy?.[0]?.order as SortOrder,
    searchString,
    props.filters as any,
  );
};

watch(
  () => props.itemsToPrepend,
  (newPrependItems) => {
    listItems.value = [
      ...newPrependItems,
      ...Object.values(fetchedPages.value)
        .flat()
        .map((user) => ({
          ...user,
          _hidden:
            !props.searchString && // only hide when not searching
            newPrependItems.some((item) => item.uid === user.uid),
        })),
    ];
  },
  { immediate: true },
);

onMounted(() => {
  setupInfiniteScrolling();
});

const fetchItems = async (
  page: number,
  itemsPerPage: number,
  sortBy: string | undefined,
  sortOrder: SortOrder | undefined,
  searchString: string | undefined,
  filters: Record<string, any>,
) => {
  internalLoading.value = true;
  scrolling.value = page > 1;
  return props
    .fetchItems({
      pageNumber: page,
      pageSize: itemsPerPage,
      sortBy,
      sortOrder,
      q: searchString,
      filters,
    })
    .then((response) => {
      fetchedPages.value = { ...fetchedPages.value, [page]: (response as any).data ?? response };
      listItems.value = [
        ...props.itemsToPrepend,
        ...Object.values(fetchedPages.value)
          .flat()
          .map((user) => ({
            ...user,
            _hidden:
              !searchString && // only hide when not searching
              props.itemsToPrepend.some((item) => item.uid === user.uid),
          })),
      ];
      listItemsLength.value = (response as any).meta?.totalItems ?? 0;
      internalLoading.value = false;
      scrolling.value = false;
    })
    .catch((error) => {
      console.error('Error loading Items', error);
      internalLoading.value = false;
      scrolling.value = false;
    });
};

const setupInfiniteScrolling = () => {
  const dataTableScrolling = useScroll(theTable.value?.$el.querySelector('.v-table__wrapper'));
  watch(
    () => dataTableScrolling.arrivedState.bottom,
    async (isBottom: boolean) => {
      if (!isBottom) {
        return;
      }
      if (listItems.value.length >= listItemsLength.value) {
        return;
      }

      scrolling.value = true;
      pageNumber.value += 1;
      scrolling.value = false;
    },
  );
};

// Create a reactive object to watch for changes in organizationUid, searchString, and filters
// Anything that changes here will trigger a refetch of the table data from page 1
const searchObject: Ref<SearchObject> = ref({
  organizationUid: props.organizationUid,
  searchString: props.searchString,
  filters: props.filters,
});

const triggerTableRefetch = (newSearchObject: SearchObject) => {
  pageNumber.value = 1;
  fetchedPages.value = {};
  searchObject.value = newSearchObject;
};
watch(
  () => props.searchString,
  (newValue) => {
    triggerTableRefetch({ ...searchObject.value, searchString: newValue });
  },
);
watch(
  () => props.organizationUid,
  (newValue) => {
    triggerTableRefetch({ ...searchObject.value, organizationUid: newValue });
  },
);
watch(
  () => props.filters,
  (newValue) => {
    triggerTableRefetch({ ...searchObject.value, filters: newValue });
  },
  { deep: true },
);

const searchQuery = computed(() => {
  return JSON.stringify(searchObject.value);
});

// @ts-ignore
type RowPropsData = Parameters<NonNullable<VDataTable['$props']['rowProps']>>[number];
const rowProps = (data: RowPropsData) => {
  return {
    class: {
      // @ts-ignore
      'selected-item': selectedItems.value.includes(data.item[props.itemValue]),
      // @ts-ignore
      'hidden-item': data.item._hidden,
    },
  };
};
</script>
<template>
  <v-data-table-server
    ref="theTable"
    v-model="selectedItems"
    :items="listItems"
    :item-value="itemValue"
    :items-length="listItemsLength"
    :items-per-page="itemsPerPage"
    :header-props="{ class: 'text-capitalize', sstyle: 'opacity: 1' }"
    :sort-by="sortBy && sortOrder ? [{ key: sortBy, order: sortOrder }] : undefined"
    :page="pageNumber"
    :filters="filters"
    :search="searchQuery"
    :loading="isLoading && !scrolling"
    :multi-sort="false"
    :hover="true"
    :height="height"
    fixed-header
    hide-default-footer
    :row-props="rowProps"
    @update:options="optionsUpdated"
    @update:sort-by="sortingUpdated"
    v-bind="$attrs"
  >
    <template v-for="(s, slotName) in $slots as object" :key="slotName" #[slotName]="slotProps">
      <slot :name="slotName" v-bind="slotProps ?? {}" />
    </template>

    <!-- hide select all from header -->
    <template #[`header.data-table-select`]></template>

    <template #[`loader`]>
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </template>

    <!-- scrolling indicator -->
    <template v-if="scrolling" #[`body.append`]>
      <tr>
        <td colspan="10" style="height: 2px; padding: 0">
          <v-progress-linear height="2px" indeterminate color="primary"></v-progress-linear>
        </td>
      </tr>
    </template>
  </v-data-table-server>
</template>

<style scoped lang="scss">
:deep(.selected-item) {
  background-color: rgb(var(--v-theme-primary-container));
}

:deep(.hidden-item) {
  display: none;
}
</style>
